<template>
  <div class="modal-body">
    <form @submit.prevent="handleSubmit">
      <!-- Step Indicator -->
      <div class="mb-4 text-center">
        <span :class="['step-badge', step === 1 ? 'active' : '']">1</span>
        <span class="mx-2">—</span>
        <span :class="['step-badge', step === 2 ? 'active' : '']">2</span>
        <span class="mx-2">—</span>
        <span :class="['step-badge', step === 3 ? 'active' : '']">3</span>
      </div>

      <!-- STEP 1: Grade & Subject -->
      <div v-if="step === 1">
        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

        <div class="mb-3">
          <label class="form-label">Grade</label>
          <select class="form-select" v-model="newPayment.grade_id" required>
            <option value="" disabled>Select Grade</option>
            <option v-for="g in grades" :key="g.id" :value="g.id">
              {{ g.level }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">Subject</label>
          <select
            class="form-select"
            v-model="newPayment.subject_id"
            :disabled="!newPayment.grade_id"
            required
          >
            <option value="" disabled>Select Subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- STEP 2: Student & Month -->
      <div v-if="step === 2">
        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

        <div class="mb-3">
          <label class="form-label">Student</label>
          <select
            class="form-select"
            v-model="newPayment.student_id"
            :disabled="!newPayment.grade_id || !newPayment.subject_id"
            required
          >
            <option value="" disabled>Select Student</option>
            <option
              v-for="student in students"
              :key="student.id"
              :value="student.id"
            >
              {{ student.first_name }} {{ student.last_name }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">Payment Date</label>
          <input
            type="date"
            class="form-control"
            v-model="newPayment.payment_date"
            required
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Valid Until</label>
          <input
            type="date"
            class="form-control"
            v-model="newPayment.valid_until"
          />
        </div>
      </div>

      <!-- STEP 3: Payment Details -->
      <div v-if="step === 3">
        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

        <div class="mb-3">
          <label class="form-label">Amount</label>
          <input
            type="number"
            class="form-control"
            v-model="newPayment.amount"
            required
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="newPayment.status" required>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">Payment Method</label>
          <select
            class="form-select"
            v-model="newPayment.payment_method"
            required
          >
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
            <option value="online">Online</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">Transaction ID (optional)</label>
          <input
            type="text"
            class="form-control"
            v-model="newPayment.transaction_id"
          />
        </div>
      </div>

      <!-- FOOTER -->
      <div class="modal-footer p-0 mt-3">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
          Close
        </button>

        <button
          v-if="step > 1"
          type="button"
          class="btn btn-outline-secondary"
          @click="step--"
        >
          Back
        </button>

        <button
          v-if="step < 3"
          type="button"
          class="btn btn-primary"
          @click="nextStep"
        >
          Next
        </button>

        <button
          v-if="step === 3"
          type="submit"
          class="btn btn-pink"
          :disabled="loading"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          {{ loading ? "Saving..." : "Save" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AddPayment",
  data() {
    return {
      step: 1,
      grades: [],
      subjects: [],
      students: [],

      newPayment: {
        grade_id: "",
        subject_id: "",
        student_id: "",
        month: "",
        amount: 0,
        payment_date: "",
        valid_until: "",
        status: "pending",
        transaction_id: "",
        payment_method: "cash",
      },
      loading: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  mounted() {
    this.fetchGrades();
  },
  watch: {
    "newPayment.grade_id"() {
      this.fetchSubjectsByGrade();
      this.newPayment.student_id = "";
    },
    "newPayment.subject_id"() {
      this.fetchStudentsByGradeSubject();
      this.newPayment.student_id = "";
    },
  },
  methods: {
    nextStep() {
      if (
        this.step === 1 &&
        (!this.newPayment.grade_id || !this.newPayment.subject_id)
      ) {
        this.errorMessage = "Please select a grade and subject first.";
        return;
      }
      if (this.step === 2 && !this.newPayment.student_id) {
        this.errorMessage = "Please select a student.";
        return;
      }
      this.errorMessage = "";
      this.step++;
    },
    async handleSubmit() {
      try {
        this.loading = true;
        await axios.post("/add-payments", this.newPayment);
        this.successMessage = "Payment added successfully!";
        this.$emit("payment-added");
        this.resetForm();
      } catch (err) {
        console.error(err);
        this.errorMessage = "Failed to save payment.";
      } finally {
        this.loading = false;
      }
    },
    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },
    async fetchSubjectsByGrade() {
      if (!this.newPayment.grade_id) return;
      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.newPayment.grade_id}`,
        );
        this.subjects = res.data.subjects || res.data;
      } catch (err) {
        console.error(err);
        this.subjects = [];
      }
    },
    async fetchStudentsByGradeSubject() {
      if (!this.newPayment.grade_id || !this.newPayment.subject_id) return;
      try {
        const res = await axios.get("/students-by-grade-subject", {
          params: {
            grade_id: this.newPayment.grade_id,
            subject_id: this.newPayment.subject_id,
          },
        });
        this.students = res.data;
      } catch (err) {
        console.error(err);
        this.students = [];
      }
    },
    resetForm() {
      this.step = 1;
      this.newPayment = {
        grade_id: "",
        subject_id: "",
        student_id: "",
        month: "",
        amount: 0,
        payment_date: "",
        valid_until: "",
        status: "pending",
        transaction_id: "",
        payment_method: "cash",
      };
      this.subjects = [];
      this.students = [];
      this.errorMessage = "";
      this.successMessage = "";
    },
  },
};
</script>

<style scoped lang="scss">
.step-badge {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ddd;
  line-height: 30px;
  font-weight: bold;
}
.step-badge.active {
  background: #e83e8c;
  color: white;
}
.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}
</style>
